// 크리에이터 구좌 대시보드 — 실 API 연동 (commerce admin BFF, localhost:8085).
//   GET /api/creator-slot-dashboard            (month·label·salesforceCreatorId·businessType)
//   GET /api/creator-slot-dashboard/filter-options
// 인증은 /platform 과 동일한 Bearer 토큰(localStorage)을 공유한다.
//
// ── 하이브리드(방향 C) ─────────────────────────────────────────────
// 백엔드는 businessType(longform|shortform)을 "한 번에 하나씩" 반환한다.
// 타입 열(롱·숏)을 한 행에 나란히 놓기 위해 두 타입을 병렬 호출해 creatorId로 병합한다.
//   · 롱폼·숏폼 = 실데이터 · 롱+숏 합산 소진율 = 실데이터
//   · 라이브커머스·등급·담당자 = 백엔드 연동 전 "미리보기" 목 데이터 (mock=true로 표시)
//   · 토큰 없음(로컬) = 전체 목 데이터로 디자인 미리보기
import { ApiError, getLecommendToken } from '../platform/creatorBusiness'

export { ApiError, getLecommendToken } from '../platform/creatorBusiness'

// Vite dev 프록시(/lecommend-api → localhost:8085/api)를 거쳐 same-origin(5173)으로 호출 → CORS 회피.
const BASE_URL = '/creator-slot-dashboard'

export type BusinessType = 'longform' | 'shortform'
export type WeekIndex = 1 | 2 | 3 | 4 | 5
export const WEEKS: WeekIndex[] = [1, 2, 3, 4, 5]
export const BUSINESS_TYPE_LABELS: Record<BusinessType, string> = { longform: '롱폼', shortform: '숏폼' }

// ── 타입 열(롱·숏) — 라이브커머스는 일단 생략 ──
export type SlotType = 'long' | 'short'
export const SLOT_TYPES: SlotType[] = ['long', 'short']
export const SLOT_TYPE_LABELS: Record<SlotType, string> = {
  long: '롱폼',
  short: '숏폼',
}
// 소진율은 롱+숏 (스펙 R8)
export const BURN_TYPES: SlotType[] = ['long', 'short']

// 등급 버킷 (구독자수 — 스펙 R14). 실데이터 소스 확정 전 미리보기.
export type Tier = '마이크로' | '미들' | '탑' | '메가'

export interface TypeRoll {
  target: number
  closed: number
  remaining: number
  capacityResolved: boolean // 분모(정원) 확정 여부. false면 전체·잔여를 '–'로
  weekly: number[] // 길이 5 (1~5주 마감 수)
  mock?: boolean // true = 백엔드 연동 전 미리보기 값
}

export interface CreatorRow {
  creatorId: string
  name: string
  label: string
  tier: Tier | null // 구독자수 버킷 — 미리보기(mock)
  assignee: string | null // 미리보기(mock)
  byType: Record<SlotType, TypeRoll | null> // null = 목표없음/깨짐 → '—'
  insta: 'unlimited'
  mockMeta?: boolean // tier·assignee가 미리보기 값이면 true
}

export interface DashboardSummary {
  total: number // 롱+숏 목표 합
  closed: number // 롱+숏 마감 합
  remaining: number // 롱+숏 잔여 합
  burnRate: number // 0~1 (롱+숏)
}

export interface CreatorSlotsDashboard {
  month: string
  rows: CreatorRow[]
  summary: DashboardSummary
  preview: boolean // true = 전체 목 데이터(토큰 없음) 미리보기
}

export interface CreatorSlotsRequest {
  month: string // 'YYYY-MM'
  label?: string
  creatorId?: string // = salesforceCreatorId
}

// ── 최근 N개월 옵션 (YYYY-MM) ──
export function recentMonths(count = 12): string[] {
  const now = new Date()
  const out: string[] = []
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }
  return out
}

interface Envelope<T> {
  success: boolean
  data: T | null
  errorMessage?: string
}

async function apiGet<T>(path: string, params?: Record<string, string | undefined>): Promise<T> {
  const qs = new URLSearchParams()
  if (params) for (const [k, v] of Object.entries(params)) if (v) qs.append(k, v)
  const token = getLecommendToken()
  const res = await fetch(`${path}${qs.toString() ? `?${qs}` : ''}`, {
    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  })
  let body: Envelope<T> | null = null
  try {
    body = (await res.json()) as Envelope<T>
  } catch {
    /* non-JSON */
  }
  if (res.status === 401)
    throw new ApiError('인증에 실패했습니다. 서버의 lecommend 서비스 토큰(COMMERCE_SERVICE_TOKEN) 설정을 확인하세요.', 401)
  if (!res.ok || !body?.success) throw new ApiError(body?.errorMessage ?? `요청 실패 (${res.status})`, res.status)
  return body.data as T
}

async function apiPut(path: string, payload: unknown): Promise<void> {
  const token = getLecommendToken()
  const res = await fetch(path, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  })
  let body: Envelope<unknown> | null = null
  try {
    body = (await res.json()) as Envelope<unknown>
  } catch {
    /* non-JSON */
  }
  if (res.status === 401) throw new ApiError('인증이 필요합니다. (lecommend 토큰 필요)', 401)
  if (!res.ok || !body?.success) throw new ApiError(body?.errorMessage ?? `저장 실패 (${res.status})`, res.status)
}

// ── 실 API 응답 타입 (백엔드 View) ──
interface ApiCreator {
  salesforceCreatorId: string
  name: string
  label: string
  tier?: string | null // 구독자수 버킷 (백엔드 CreatorChannelScale) — 없으면 '—'
  total: number
  closed: number
  remaining: number
  capacityResolved: boolean
  weeklyClosed: number[]
}

// 백엔드가 준 tier 라벨을 검증(모르는 값이면 null → '—').
function apiTier(v: unknown): Tier | null {
  return v === '마이크로' || v === '미들' || v === '탑' || v === '메가' ? v : null
}
interface ApiDashboard {
  startDate: string
  endDate: string
  businessType: string
  summary: { total: number; closed: number; remaining: number; closedRatePercent: number }
  labels: { label: string; creators: ApiCreator[] }[]
}

function weekly5(arr: number[] | undefined): number[] {
  const a = arr ?? []
  return [a[0] ?? 0, a[1] ?? 0, a[2] ?? 0, a[3] ?? 0, a[4] ?? 0]
}

function rollFromApi(c: ApiCreator): TypeRoll {
  return {
    target: c.total,
    closed: c.closed,
    remaining: c.remaining,
    capacityResolved: c.capacityResolved,
    weekly: weekly5(c.weeklyClosed),
  }
}

// ── 결정론적 미리보기(목) 헬퍼 — creatorId/label 기반이라 새로고침해도 값 고정 ──
function hash(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return Math.abs(h)
}
const TIERS: Tier[] = ['마이크로', '마이크로', '미들', '미들', '탑', '메가']
const ASSIGNEES = ['윤인혜', '김민주', '전수현', '한희진']
function mockTier(creatorId: string): Tier {
  return TIERS[hash('tier' + creatorId) % TIERS.length]
}
function mockAssignee(label: string): string {
  return ASSIGNEES[hash('assignee' + label) % ASSIGNEES.length]
}

function combineSummary(rows: CreatorRow[]): DashboardSummary {
  let total = 0
  let closed = 0
  let remaining = 0
  for (const r of rows) {
    for (const t of ['long', 'short'] as SlotType[]) {
      const roll = r.byType[t]
      if (!roll || roll.target <= 0) continue
      total += roll.target
      closed += Math.min(roll.closed, roll.target)
      remaining += Math.max(0, roll.target - roll.closed)
    }
  }
  return { total, closed, remaining, burnRate: total > 0 ? closed / total : 0 }
}

/**
 * 대시보드 조회 — 롱폼·숏폼을 병렬 호출해 creatorId로 병합.
 * 라이브·등급·담당자는 미리보기(목). 401이면 상위에서 catch 후 mock 미리보기로 폴백.
 */
export async function fetchCreatorSlotsDashboard(params: CreatorSlotsRequest): Promise<CreatorSlotsDashboard> {
  const query = (bt: BusinessType) =>
    apiGet<ApiDashboard>(BASE_URL, {
      month: params.month,
      businessType: bt,
      label: params.label,
      salesforceCreatorId: params.creatorId,
    })

  const [longD, shortD] = await Promise.all([query('longform'), query('shortform')])

  const merged = new Map<string, CreatorRow>()
  const ingest = (d: ApiDashboard, type: 'long' | 'short') => {
    for (const g of d.labels ?? []) {
      for (const c of g.creators ?? []) {
        const id = c.salesforceCreatorId
        let row = merged.get(id)
        if (!row) {
          row = {
            creatorId: id,
            name: c.name,
            label: c.label ?? g.label,
            tier: apiTier(c.tier), // 실데이터 (SF 구독자수 버킷)
            assignee: null,
            byType: { long: null, short: null },
            insta: 'unlimited',
          }
          merged.set(id, row)
        }
        row.byType[type] = rollFromApi(c)
      }
    }
  }
  ingest(longD, 'long')
  ingest(shortD, 'short')

  const rows = [...merged.values()]
  return { month: params.month, rows, summary: combineSummary(rows), preview: false }
}

// ── 편집 저장 (변경된 값만 PUT) ──
// 목표(target)가 바뀌면 그 월부터 적용(effective-dated), 주차 마감이 바뀐 칸만 override.
export interface SlotEditDraft {
  target: number
  weekly: number[]
}
export async function saveCreatorSlotEdit(params: {
  creatorId: string
  month: string
  original: Record<SlotType, { target: number; weekly: number[] } | null>
  draft: Record<SlotType, SlotEditDraft | null>
}): Promise<void> {
  const capacities: { businessType: string; capacity: number }[] = []
  const closedOverrides: { businessType: string; week: number; closed: number }[] = []
  const bt = (t: SlotType): string => (t === 'long' ? 'longform' : 'shortform')

  for (const t of ['long', 'short'] as SlotType[]) {
    const d = params.draft[t]
    if (!d) continue
    const o = params.original[t]
    if (!o || d.target !== o.target) capacities.push({ businessType: bt(t), capacity: d.target })
    const ow = o?.weekly ?? []
    d.weekly.forEach((v, i) => {
      if (v !== (ow[i] ?? 0)) closedOverrides.push({ businessType: bt(t), week: i + 1, closed: v })
    })
  }

  if (capacities.length === 0 && closedOverrides.length === 0) return
  await apiPut(`${BASE_URL}/edit`, {
    salesforceCreatorId: params.creatorId,
    month: params.month,
    capacities,
    closedOverrides,
  })
}

// ── 필터 옵션 ──
export interface SlotFilterOptions {
  labels: string[]
  creators: { id: string; name: string; label: string }[]
}
interface ApiFilterOptions {
  labels: string[]
  creators: { salesforceCreatorId: string; name: string; label: string }[]
  businessTypes: { code: string; label: string }[]
}
export async function fetchCreatorSlotsFilterOptions(): Promise<SlotFilterOptions> {
  const d = await apiGet<ApiFilterOptions>(`${BASE_URL}/filter-options`)
  return {
    labels: d.labels ?? [],
    creators: (d.creators ?? []).map((c) => ({ id: c.salesforceCreatorId, name: c.name, label: c.label })),
  }
}

// ── 전체 미리보기(목) 데이터 — 토큰 없이 로컬에서 디자인 확인용 ──
// 실제 레이블 기본 구좌 시트 기준(롱폼·숏폼 목표 슬롯). 마감은 SF 값이라 계속 목.
const PREVIEW_SEED: { name: string; label: string; long: number; short: number }[] = [
  { name: '민정박', label: 'LBE', long: 4, short: 2 },
  { name: '라일리', label: 'LBE', long: 3, short: 2 },
  { name: '훠니', label: 'LBE', long: 3, short: 2 },
  { name: '민스코', label: 'LBE', long: 4, short: 4 },
  { name: '보현', label: 'LBE', long: 3, short: 2 },
  { name: '달님', label: 'LBE', long: 3, short: 2 },
  { name: '홀리', label: 'LBE', long: 3, short: 3 },
  { name: '재리', label: 'LBE', long: 2, short: 2 },
  { name: '데일리제나', label: 'LBE', long: 3, short: 3 },
  { name: '솔미', label: 'LBE', long: 3, short: 3 },
  { name: '리수', label: 'LBE', long: 2, short: 2 },
  { name: '하이예나', label: 'LBE', long: 3, short: 3 },
  { name: '헤이즐', label: 'LBE', long: 4, short: 3 },
  { name: '샒의삶', label: 'LBE', long: 4, short: 3 },
  { name: '데이지', label: 'LBE', long: 3, short: 3 },
  { name: '밤비걸', label: 'LBE', long: 0, short: 4 },
  { name: '기은세의 집', label: 'Le.dinary', long: 2, short: 2 },
  { name: '다샤', label: 'Le.dinary', long: 1, short: 5 },
  { name: '미지우 MEJIWOO', label: 'Le.dinary', long: 1, short: 2 },
  { name: '미희', label: 'Le.dinary', long: 3, short: 3 },
  { name: '빛날영', label: 'Le.dinary', long: 3, short: 3 },
  { name: '상희', label: 'Le.dinary', long: 2, short: 3 },
  { name: '윤그린', label: 'Le.dinary', long: 2, short: 3 },
  { name: '잉화', label: 'Le.dinary', long: 2, short: 5 },
  { name: '조나단', label: 'Le.dinary', long: 0, short: 4 },
  { name: '지현서', label: 'Le.dinary', long: 4, short: 3 },
  { name: '채소', label: 'Le.dinary', long: 3, short: 3 },
  { name: '최상아 Sangah Choi', label: 'Le.dinary', long: 1, short: 3 },
  { name: '털난새우', label: 'Le.dinary', long: 3, short: 5 },
  { name: '희주', label: 'Le.dinary', long: 2, short: 7 },
  { name: '우나', label: 'Le.dinary', long: 0, short: 0 },
  { name: '유진an', label: 'Le.dinary', long: 0, short: 0 },
  { name: '김습습', label: 'CAAPE', long: 0, short: 3 },
  { name: '아랑', label: 'CAAPE', long: 3, short: 3 },
  { name: '킴닥스', label: 'CAAPE', long: 2, short: 2 },
  { name: '민카롱', label: 'CAAPE', long: 4, short: 4 },
  { name: '김크리스탈', label: 'CAAPE', long: 2, short: 3 },
  { name: '유리아', label: 'CAAPE', long: 2, short: 2 },
  { name: '소봉', label: 'CAAPE', long: 0, short: 3 },
  { name: '미아', label: 'CAAPE', long: 3, short: 3 },
  { name: '몌', label: 'CAAPE', long: 2, short: 2 },
  { name: '은조미', label: 'CAAPE', long: 0, short: 0 },
  { name: '윤이든', label: 'CAAPE', long: 3, short: 3 },
  { name: '춤추는 선진이', label: 'CAAPE', long: 2, short: 2 },
  { name: '김아야', label: 'CAAPE', long: 0, short: 4 },
  { name: '온드', label: 'CAAPE', long: 0, short: 2 },
  { name: '다인', label: 'CAAPE', long: 1, short: 4 },
  { name: '레오제이', label: 'DAEAD', long: 3, short: 3 },
]

function mockRealRoll(creatorId: string, salt: string, cap: number): TypeRoll | null {
  const h = hash(salt + creatorId)
  if (cap === 0) return null // 목표 미설정 → '—'
  // 대략 0~84% 소진 → 목표가 꽉 차기보다 어느정도 빈 구좌(점선 +)가 남게
  const pct = (h % 85) / 100
  const closed = Math.min(cap, Math.round(cap * pct))
  const weekly = [0, 0, 0, 0, 0]
  let left = closed
  let wi = h % 5
  while (left > 0) {
    weekly[wi % 5] += 1
    wi = (wi + 1 + (h % 3)) % 5
    left -= 1
  }
  return { target: cap, closed, remaining: cap - closed, capacityResolved: true, weekly, mock: true }
}

export function previewDashboard(month: string, opts?: { label?: string; creatorId?: string }): CreatorSlotsDashboard {
  const rows: CreatorRow[] = PREVIEW_SEED.map((s, i) => {
    const id = `preview-${i}`
    return {
      creatorId: id,
      name: s.name,
      label: s.label,
      tier: mockTier(id),
      assignee: mockAssignee(s.label),
      byType: {
        // 목표(target) = 시트 실값 · 마감(closed)만 결정론적 목
        long: mockRealRoll(id, 'long' + month, s.long),
        short: mockRealRoll(id, 'short' + month, s.short),
      },
      insta: 'unlimited',
      mockMeta: true,
    }
  })
  const filtered = rows.filter(
    (r) => (!opts?.label || r.label === opts.label) && (!opts?.creatorId || r.creatorId === opts.creatorId),
  )
  return { month, rows: filtered, summary: combineSummary(filtered), preview: true }
}

export function previewFilterOptions(): SlotFilterOptions {
  const labels = [...new Set(PREVIEW_SEED.map((s) => s.label))]
  const creators = PREVIEW_SEED.map((s, i) => ({ id: `preview-${i}`, name: s.name, label: s.label }))
  return { labels, creators }
}
